import React from 'react';
import './PrintableOrder.css'; // Assuming a similar CSS file structure
import { format } from 'date-fns';

// Interfaces based on data structure likely returned by getOrderById
interface OrderItemPrint {
  id: number;
  Structure: { // Expect Structure (capital S) based on Sequelize alias
    code: string;
    type: string;
    location: string;
  };
  startDate: Date | string;
  endDate: Date | string;
  price: number; // Assuming unit price
  discount?: number | null; // Assuming discount percentage or amount
  total: number; // Assuming final price for item
}

interface ClientInfoPrint {
  name: string;
  director?: string | null;
  phone?: string | null;
  email?: string | null;
  address?: string | null;
}

interface OrderDataPrint {
  id: number;
  orderNumber: string;
  orderDate: Date | string; // Assuming createdAt or a specific orderDate field
  createdAt: Date | string; // Add createdAt back for fallback logic in print page
  Client: ClientInfoPrint; // Correct casing
  status: string;
  totalAmount: number; // Sum before discount
  discount: number; // Overall discount amount/percentage? Needs clarification from controller
  finalAmount: number; // Final total
  paidAmount: number;
  paymentTerms?: string | null;
  notes?: string | null;
  OrderItems: OrderItemPrint[];
}

interface PrintableOrderProps {
  orderData: OrderDataPrint;
}

// Helper to format dates
const formatDate = (date: Date | string | null | undefined): string => {
  if (!date) return 'N/A';
  try {
    return format(new Date(date), 'dd.MM.yyyy');
  } catch (e) {
    return 'Invalid Date';
  }
};

// Helper to format currency
const formatCurrency = (value: number | null | undefined): string => {
  if (value === undefined || value === null) return '0 ₸';
  return new Intl.NumberFormat('ru-KZ', {
    style: 'currency',
    currency: 'KZT',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
};

// Helper for status text
const getStatusText = (status: string): string => {
  const statuses: { [key: string]: string } = {
    booked: 'Забронирован',
    waitingPayment: 'Ожидает оплаты',
    partiallyPaid: 'Частично оплачен',
    fullyPaid: 'Полностью оплачен',
    inProgress: 'В работе', // Assuming this might be a status
    completed: 'Завершен',
    cancelled: 'Отменен',
  };
  return statuses[status] || status;
};

const PrintableOrder: React.FC<PrintableOrderProps> = ({ orderData }) => {
  if (!orderData) {
    return <div className="printable-container">Ошибка: Данные для заказа не найдены.</div>;
  }

  const {
    orderNumber,
    orderDate,
    Client, // Use the correct casing from the fetched data
    status,
    totalAmount,
    discount, // How is this used? Assuming it's an amount for now.
    finalAmount,
    paidAmount,
    paymentTerms,
    notes,
    OrderItems,
  } = orderData;

  const balanceDue = finalAmount - paidAmount;

  return (
    <div className="printable-container order-container">
      <header className="printable-header">
        <h1>Заказ № {orderNumber}</h1>
        <p>Дата: {formatDate(orderDate)}</p>
        <p>Статус: {getStatusText(status)}</p>
      </header>

      <section className="client-info">
        <h2>Информация о клиенте</h2>
        <p><strong>Компания:</strong> {Client.name}</p>
        {Client.director && <p><strong>Директор:</strong> {Client.director}</p>}
        {Client.phone && <p><strong>Телефон:</strong> {Client.phone}</p>}
        {Client.email && <p><strong>Email:</strong> {Client.email}</p>}
        {Client.address && <p><strong>Адрес:</strong> {Client.address}</p>}
      </section>

      <section className="items-table">
        <h2>Позиции заказа</h2>
        <table>
          <thead>
            <tr>
              <th>№</th>
              <th>Конструкция (Код)</th>
              <th>Тип</th>
              <th>Локация</th>
              <th>Период</th>
              <th>Цена (₸)</th>
              {/*<th>Скидка</th>*/}
              <th>Итого (₸)</th>
            </tr>
          </thead>
          <tbody>
            {OrderItems.map((item, index) => (
              <tr key={item.id}>
                <td>{index + 1}</td>
                {/* Safely access Structure properties */}
                <td>{item.Structure?.code || 'N/A'}</td>
                <td>{item.Structure?.type || 'N/A'}</td>
                <td>{item.Structure?.location || 'N/A'}</td>
                <td>{formatDate(item.startDate)} - {formatDate(item.endDate)}</td>
                <td>{formatCurrency(item.price)}</td>
                {/*<td>{item.discount ? `${item.discount}%` : '-'}</td>*/}
                <td>{formatCurrency(item.total)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      <section className="summary">
        <h2>Итоги</h2>
        <div className="summary-details">
          <p><strong>Сумма позиций:</strong> {formatCurrency(totalAmount)}</p>
          {/* <p><strong>Скидка:</strong> {formatCurrency(discount)}</p> */}
          <p><strong>Итого к оплате:</strong> {formatCurrency(finalAmount)}</p>
          <p><strong>Оплачено:</strong> {formatCurrency(paidAmount)}</p>
          <p><strong>Остаток к оплате:</strong> {formatCurrency(balanceDue)}</p>
        </div>
      </section>

      {paymentTerms && (
        <section className="payment-terms">
          <h2>Условия оплаты</h2>
          <p>{paymentTerms}</p>
        </section>
      )}

      {notes && (
        <section className="notes">
          <h2>Примечания</h2>
          <p>{notes}</p>
        </section>
      )}

      <footer className="printable-footer">
        {/* Footer content if needed */}
      </footer>
    </div>
  );
};

export default PrintableOrder;
